import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ShieldCheck,
  RefreshCw,
  Download,
  Search,
  Check,
  X,
  LogOut,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'reset_admin_token';

const QUESTION_LABELS = {
  retirement_plan: 'Retirement',
  college_savings: 'College',
  reduce_debt: 'Debt',
  life_insurance: 'Insurance',
  start_investing: 'Investing',
};

export default function AdminSurveys() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialToken =
    searchParams.get('token') || localStorage.getItem(TOKEN_KEY) || '';

  const [token, setToken] = useState(initialToken);
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | retirement_plan | ...

  const fetchResponses = async (t) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/survey/wealth`, {
        headers: { 'X-Admin-Token': t },
      });
      if (res.status === 401) {
        setError('Invalid admin token. Try again.');
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
        return;
      }
      if (!res.ok) {
        setError(`Error ${res.status}. Please retry.`);
        return;
      }
      const data = await res.json();
      setRows(data.responses || []);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
      // Clean token from URL so it doesn't get shared accidentally
      if (searchParams.get('token')) {
        searchParams.delete('token');
        setSearchParams(searchParams, { replace: true });
      }
    } catch (e) {
      setError('Network error. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-attempt fetch on mount if a token is already saved
  useEffect(() => {
    if (initialToken) fetchResponses(initialToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    fetchResponses(token.trim());
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthed(false);
    setRows([]);
  };

  const filtered = useMemo(() => {
    let r = rows;
    if (filter !== 'all') r = r.filter((row) => row[filter] === true);
    if (query.trim()) {
      const q = query.toLowerCase();
      r = r.filter(
        (row) =>
          (row.name || '').toLowerCase().includes(q) ||
          (row.email || '').toLowerCase().includes(q) ||
          (row.phone || '').toLowerCase().includes(q),
      );
    }
    return r;
  }, [rows, query, filter]);

  const yesCounts = useMemo(() => {
    const acc = {
      retirement_plan: 0,
      college_savings: 0,
      reduce_debt: 0,
      life_insurance: 0,
      start_investing: 0,
    };
    rows.forEach((r) => {
      Object.keys(acc).forEach((k) => {
        if (r[k]) acc[k] += 1;
      });
    });
    return acc;
  }, [rows]);

  const downloadCsv = () => {
    const header = [
      'Name',
      'Email',
      'Phone',
      ...Object.values(QUESTION_LABELS),
      'Submitted',
    ];
    const lines = [header.join(',')];
    filtered.forEach((r) => {
      const row = [
        `"${(r.name || '').replace(/"/g, '""')}"`,
        `"${(r.email || '').replace(/"/g, '""')}"`,
        `"${(r.phone || '').replace(/"/g, '""')}"`,
        r.retirement_plan ? 'Yes' : 'No',
        r.college_savings ? 'Yes' : 'No',
        r.reduce_debt ? 'Yes' : 'No',
        r.life_insurance ? 'Yes' : 'No',
        r.start_investing ? 'Yes' : 'No',
        r.created_at || '',
      ];
      lines.push(row.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wealth-surveys-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- Auth gate ---
  if (!authed) {
    return (
      <div
        className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6"
        data-testid="admin-login-page"
      >
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md bg-[#111111] border border-white/10 rounded-2xl p-8 md:p-10"
          data-testid="admin-login-form"
        >
          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="font-cormorant text-3xl font-medium text-white text-center mb-2">
            Admin Access
          </h1>
          <p className="text-zinc-400 font-manrope text-sm text-center mb-6">
            Enter your admin token to view survey responses.
          </p>
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12 mb-4"
            data-testid="admin-token-input"
            autoFocus
          />
          {error && (
            <p
              className="text-red-400 font-manrope text-sm mb-4 text-center"
              data-testid="admin-login-error"
            >
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading || !token.trim()}
            className="w-full h-12 bg-[#D4AF37] text-[#0A0A0A] rounded-full font-manrope font-semibold hover:bg-amber-200 disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? 'Verifying…' : 'Unlock'}
          </Button>
        </form>
      </div>
    );
  }

  // --- Authed dashboard ---
  const total = rows.length;

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white"
      data-testid="admin-surveys-page"
    >
      {/* Header */}
      <header className="border-b border-white/5 bg-[#0A0A0A]/90 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center font-cormorant font-bold text-lg">
              R
            </div>
            <div>
              <p className="text-white font-cormorant text-lg leading-tight">
                Reset Admin
              </p>
              <p className="text-zinc-500 font-manrope text-[10px] uppercase tracking-widest">
                Wealth Survey Responses
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/admin/feedback"
              className="text-zinc-400 hover:text-white font-manrope text-sm transition-colors"
            >
              Retreat feedback →
            </a>
            <button
              onClick={() => fetchResponses(token)}
              disabled={loading}
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-white font-manrope text-sm transition-colors disabled:opacity-50"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-red-400 font-manrope text-sm transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          <StatCard label="Total responses" value={total} highlight />
          {Object.entries(QUESTION_LABELS).map(([key, label]) => (
            <StatCard
              key={key}
              label={label}
              value={yesCounts[key] || 0}
              sub={`${total ? Math.round(((yesCounts[key] || 0) / total) * 100) : 0}% Yes`}
            />
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, or phone…"
              className="pl-10 bg-[#111111] border-white/10 text-white placeholder:text-zinc-600 h-11"
              data-testid="admin-search-input"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-[#111111] border border-white/10 text-white h-11 px-4 rounded-md font-manrope text-sm focus:outline-none focus:border-[#D4AF37]/40"
            data-testid="admin-filter-select"
          >
            <option value="all">All responses</option>
            {Object.entries(QUESTION_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                Only &quot;Yes&quot; to {label}
              </option>
            ))}
          </select>
          <Button
            onClick={downloadCsv}
            disabled={filtered.length === 0}
            className="bg-[#D4AF37] text-[#0A0A0A] px-5 h-11 rounded-md font-manrope font-semibold hover:bg-amber-200 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="admin-download-csv-btn"
          >
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        </div>

        {/* Table */}
        <div className="bg-[#111111] border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-surveys-table">
              <thead>
                <tr className="border-b border-white/5 text-zinc-400 font-manrope text-xs uppercase tracking-wider">
                  <Th>Name</Th>
                  <Th>Contact</Th>
                  {Object.values(QUESTION_LABELS).map((l) => (
                    <Th key={l} center>
                      {l}
                    </Th>
                  ))}
                  <Th>Submitted</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-12 text-zinc-500 font-manrope text-sm"
                    >
                      No responses match this view.
                    </td>
                  </tr>
                )}
                {filtered.map((r, i) => (
                  <tr
                    key={r.id || i}
                    className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors"
                    data-testid={`survey-row-${i}`}
                  >
                    <Td>
                      <span className="text-white font-manrope font-medium">
                        {r.name || '—'}
                      </span>
                    </Td>
                    <Td>
                      <div className="flex flex-col gap-1">
                        {r.email && (
                          <a
                            href={`mailto:${r.email}`}
                            className="text-zinc-300 font-manrope text-sm hover:text-[#D4AF37]"
                          >
                            {r.email}
                          </a>
                        )}
                        {r.phone && (
                          <a
                            href={`tel:${r.phone}`}
                            className="text-zinc-500 font-manrope text-xs hover:text-[#D4AF37]"
                          >
                            {r.phone}
                          </a>
                        )}
                      </div>
                    </Td>
                    {Object.keys(QUESTION_LABELS).map((key) => (
                      <Td key={key} center>
                        {r[key] ? (
                          <Check className="w-4 h-4 text-[#D4AF37] mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-zinc-700 mx-auto" />
                        )}
                      </Td>
                    ))}
                    <Td>
                      <span className="text-zinc-500 font-manrope text-xs whitespace-nowrap">
                        {formatDate(r.created_at)}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-zinc-600 font-manrope text-xs text-center mt-6">
          Showing {filtered.length} of {total} responses · newest first
        </p>
      </main>
    </div>
  );
}

// --- Small helpers ---

const Th = ({ children, center }) => (
  <th
    className={`py-3 px-4 font-medium ${center ? 'text-center' : 'text-left'}`}
  >
    {children}
  </th>
);
const Td = ({ children, center }) => (
  <td className={`py-4 px-4 ${center ? 'text-center' : ''}`}>{children}</td>
);
const StatCard = ({ label, value, sub, highlight }) => (
  <div
    className={`rounded-2xl border p-4 ${
      highlight
        ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30'
        : 'bg-[#111111] border-white/10'
    }`}
  >
    <p className="text-zinc-400 font-manrope text-[11px] uppercase tracking-widest">
      {label}
    </p>
    <p className="font-cormorant text-3xl font-medium text-white mt-1">
      {value}
    </p>
    {sub && (
      <p className="text-zinc-500 font-manrope text-[11px] mt-0.5">{sub}</p>
    )}
  </div>
);

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}
